/**
 * ホワイトバランス調整モジュール（Konvaベース）
 * Konva.jsのRGBフィルターを使用した色温度調整
 */

import Konva from 'konva';

export interface WhiteBalanceOptions {
  temperature: number;  // ケルビン値 (2750K, 4750K, 6750K など)
  tint: number;        // -100 to 100 (green to magenta)
  auto?: boolean;      // 自動ホワイトバランス（現在未実装）
}

export interface CanvasFilterResult {
  blob: Blob;
  width: number;
  height: number;
  processingTime: number;
}

/**
 * Blobから画像オブジェクトを作成（data: URLを使用してCSP制限を回避）
 */
function createImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();
      const dataUrl = reader.result as string;

      img.onload = () => {
        resolve(img);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image from data URL'));
      };

      img.src = dataUrl;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read blob'));
    };

    reader.readAsDataURL(blob);
  });
}

/**
 * 色温度（ケルビン）からRGB乗算係数を計算
 * @param temperature 色温度（ケルビン単位）
 */
function getTemperatureRGBMultipliers(temperature: number): {
  red: number;
  green: number;
  blue: number;
} {
  // 色温度が0または未定義の場合は調整なし（乗算なので1.0）
  if (!temperature || temperature === 0) {
    return { red: 1.0, green: 1.0, blue: 1.0 };
  }

  // 基準値: 5500K（昼光）
  const baseTemp = 5500;
  let red = 1.0;
  let green = 1.0;
  let blue = 1.0;

  if (temperature < baseTemp) {
    // 低い色温度（暖色系）：赤みを増やし、青みを減らす
    // 2750K（電球色）〜 4750K（太陽光）〜 5500K（基準）
    const factor = (baseTemp - temperature) / 3000; // 正規化
    red = 1.0 + factor * 0.3;     // 赤を増やす（最大1.3倍）
    green = 1.0 + factor * 0.05;  // 緑を少し増やす
    blue = 1.0 - factor * 0.4;    // 青を減らす（最小0.6倍）
  } else if (temperature > baseTemp) {
    // 高い色温度（寒色系）：青みを増やし、赤みを減らす
    // 5500K（基準）〜 6750K（蛍光灯）
    const factor = (temperature - baseTemp) / 2000; // 正規化
    red = 1.0 - factor * 0.3;     // 赤を減らす（最小0.7倍）
    green = 1.0 + factor * 0.05;  // 緑を少し増やす
    blue = 1.0 + factor * 0.2;    // 青を増やす（最大1.2倍）
  }

  return { red, green, blue };
}

/**
 * ティント（色かぶり）からRGB乗算係数を計算
 * @param tint ティント値（-100 to 100、緑〜マゼンタ）
 */
function getTintRGBMultipliers(tint: number): {
  red: number;
  green: number;
  blue: number;
} {
  if (tint === 0) {
    return { red: 1.0, green: 1.0, blue: 1.0 };
  }

  const factor = tint / 100;
  return {
    red: factor > 0 ? 1.0 + factor * 0.2 : 1.0,     // マゼンタ方向で赤を増やす
    green: 1.0 - Math.abs(factor) * 0.2,            // 緑を減らす
    blue: factor > 0 ? 1.0 + factor * 0.2 : 1.0     // マゼンタ方向で青を増やす
  };
}

/**
 * ホワイトバランス調整（Konvaベース）
 */
export async function adjustWhiteBalance(
  imageBlob: Blob,
  options: WhiteBalanceOptions
): Promise<CanvasFilterResult> {
  const startTime = performance.now();

  try {
    // 何も調整しない場合は早期リターン
    if (!options.auto && (!options.temperature || options.temperature === 0) && options.tint === 0) {
      const img = await createImageFromBlob(imageBlob);
      return {
        blob: imageBlob,
        width: img.width,
        height: img.height,
        processingTime: performance.now() - startTime
      };
    }

    // 画像を読み込み
    const image = await createImageFromBlob(imageBlob);

    // 仮想コンテナを作成（実際にDOMに追加しない）
    const container = document.createElement('div');
    container.style.display = 'none';
    document.body.appendChild(container);

    // Konvaステージを作成
    const stage = new Konva.Stage({
      container: container,
      width: image.width,
      height: image.height
    });

    const layer = new Konva.Layer();

    // RGB乗算係数を計算
    const tempMultipliers = getTemperatureRGBMultipliers(options.temperature);
    const tintMultipliers = getTintRGBMultipliers(options.tint);

    // 係数を合成（乗算）
    const totalRed = tempMultipliers.red * tintMultipliers.red;
    const totalGreen = tempMultipliers.green * tintMultipliers.green;
    const totalBlue = tempMultipliers.blue * tintMultipliers.blue;

    // カスタムフィルターを定義
    const customWhiteBalance = function(imageData: ImageData) {
      const data = imageData.data;
      for (let i = 0; i < data.length; i += 4) {
        // RGB各チャンネルに乗算係数を適用
        data[i] = Math.min(255, Math.max(0, data[i] * totalRed));       // R
        data[i + 1] = Math.min(255, Math.max(0, data[i + 1] * totalGreen)); // G
        data[i + 2] = Math.min(255, Math.max(0, data[i + 2] * totalBlue));  // B
        // Alpha channel (i + 3) is unchanged
      }
    };

    // フィルターを適用したKonva画像を作成
    const konvaImage = new Konva.Image({
      x: 0,
      y: 0,
      image: image,
      filters: [customWhiteBalance]
    });

    // レイヤーに追加
    layer.add(konvaImage);
    stage.add(layer);

    // キャッシュを有効化（フィルターを適用するために必須）
    // 注意: cache()を使用するとデバイスピクセル比の影響で画像サイズが変わるが、
    // 後でリサイズ処理で元のサイズに戻す
    konvaImage.cache();

    // レイヤーを描画
    layer.draw();

    // Canvasを使用してBlobを直接取得
    return new Promise((resolve, reject) => {
      // 少し待ってから処理（描画完了を確実にする）
      setTimeout(() => {
        try {
          // Konvaのcanvas要素から直接Blobを取得
          const sourceCanvas = layer.getCanvas()._canvas;

          // Konvaが自動的にデバイスピクセル比でスケールするため、
          // 元のサイズに戻す必要がある
          if (sourceCanvas.width !== image.width || sourceCanvas.height !== image.height) {
            // 新しいcanvasを作成して元のサイズにリサイズ
            const resizedCanvas = document.createElement('canvas');
            resizedCanvas.width = image.width;
            resizedCanvas.height = image.height;
            const ctx = resizedCanvas.getContext('2d');

            if (ctx) {
              // 高品質設定でリサイズ
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              ctx.drawImage(sourceCanvas, 0, 0, image.width, image.height);

              resizedCanvas.toBlob((blob) => {
                // クリーンアップ
                stage.destroy();
                document.body.removeChild(container);

                if (blob) {
                  resolve({
                    blob: blob,
                    width: image.width,
                    height: image.height,
                    processingTime: performance.now() - startTime
                  });
                } else {
                  reject(new Error('Failed to create blob from canvas'));
                }
              }, 'image/jpeg', 0.95);
            } else {
              reject(new Error('Failed to get canvas context'));
            }
          } else {
            // サイズが一致している場合はそのまま使用
            sourceCanvas.toBlob((blob) => {
              // クリーンアップ
              stage.destroy();
              document.body.removeChild(container);

              if (blob) {
                resolve({
                  blob: blob,
                  width: image.width,
                  height: image.height,
                  processingTime: performance.now() - startTime
                });
              } else {
                reject(new Error('Failed to create blob from canvas'));
              }
            }, 'image/jpeg', 0.95);
          }
        } catch (error) {
          stage.destroy();
          document.body.removeChild(container);
          reject(error);
        }
      }, 0);
    });

  } catch (error) {
    console.error('White balance adjustment failed:', error);
    throw error;
  }
}